<script lang="ts" setup>
	import type { IconLibProps } from '../tools/icon.vue'

	defineEmits<{
		(event: 'trigger'): void
	}>()
	export type ProcessesConfigClass = {
		header: Partial<{
			base: string
			text: string
			width: string
		}>
		strokeText: Partial<{
			base: string
			translate: string
			font: string
			background: string
			text: string
			style: any
			strokeColor: string
			color: string
		}>
		iconWrapper: Partial<{
			base: string
			width: string
			height: string
			background: string
			border: string
			zIndex: string
			rounded: string
		}>
	}
	export type ProcessesStepContext = Array<{
		icon: IconLibProps
		title: string
		description: string
	}>
	const processesConfigClass: ProcessesConfigClass = {
		strokeText: {
			base: 'stroke-text absolute  block',
			translate: 'sm:-translate-x-8 sm:-translate-y-8 -translate-y-5 -translate-x-5',
			font: 'font-[700]',
			background: 'bg-clip- bg-clip-text',
			text: 'sm:text-[8em] text-[5em]',
			color: 'text-base-100 dark:text-base-700',
			strokeColor: '#9cd3c9'
		},
		iconWrapper: {
			base: ' relative left-0 -bottom-8 flex-none flexed justify-center',
			width: 'sm:w-[130px] w-[100px]',
			height: 'sm:h-[130px]  h-[100px]',
			background: 'bg-white dark:bg-gray-700',
			border: 'dark:border-gray-600 border',
			zIndex: 'z-10',
			rounded: 'rounded-full'
		},
		header: {
			text: 'text-center',
			width: 'sm:max-w-[677px]',
			base: ''
		}
	}
	const { strokeText, iconWrapper, header } = processesConfigClass

	const props = defineProps<{
		processes: ProcessesStepContext
		tag?: string
		title?: string
		description?: string
		ui?: Partial<ProcessesConfigClass>
	}>()
	const processSteps = computed(() => props.processes.length ?? 0)
</script>

<template>
	<section class="flexed flex-col justify-center w-full">
		<lazy-ui-title-header
			:ui="{
				size: 'sm:text-[3.25rem]/[3.9rem] md:text-[2.625rem]/[3.15rem] text-[2rem]/[2.8rem]',
				lineHeight: 'tracking-normal space-y-3'
			}"
			:class="[header.base, ui?.header?.text ?? header.text, ui?.header?.width ?? header.width]"
		>
			<slot name="header">
				<ui-title-tag v-if="!!tag" :tag />
				<p v-show="!title">
					Get started in
					<span
						:class="{ hidden: !processSteps, ' inline-flex': processSteps }"
						class="sm:w-[56px] sm:h-[56px] h-9 w-9 text-[1.1em] rounded-full bg-warning-20 flex-none items-center justify-center text-white"
					>
						{{ processSteps }}
					</span>
					simple steps
				</p>
				<span v-show="title">{{ title }}</span>
				<p class="text-gray-600 mt-10 text-xl font-normal mb-20">
					{{ description }}
				</p>
			</slot>
		</lazy-ui-title-header>

		<div class="flex flex-col justify-center w-full items-center gap-y-6 mt-12">
			<div class="flex-wrap lg:grid grid-cols-3 flex justify-between w-full sm:pt-10 sm:py-10 gap-8 sm:gap-5">
				<div
					v-for="(item, index) in processes"
					:key="index"
					class="flex flex-col items-center text-center flex-auto sm:min-w-[230px] sm:flex-1 sm:mr-3 last:mr-0 gap-y-3"
				>
					<div class="relative flex-none mx-auto">
						<p
							:class="[
								ui?.strokeText?.background ?? strokeText?.background,
								ui?.strokeText?.base ?? strokeText.base,
								ui?.strokeText?.font ?? strokeText.font,
								ui?.strokeText?.text ?? strokeText.text,
								ui?.strokeText?.translate ?? strokeText.translate,
								ui?.strokeText?.color ?? strokeText.color
							]"
							:style="`-webkit-text-stroke: 3px ${
								ui?.strokeText?.strokeColor ?? strokeText.strokeColor
							}; text-stroke: 3px ${ui?.strokeText?.strokeColor ?? strokeText.strokeColor}`"
						>
							{{ index + 1 }}
						</p>
						<div
							:class="[
								iconWrapper.background,
								iconWrapper.base,
								iconWrapper.border,
								iconWrapper.height,
								iconWrapper.rounded,
								iconWrapper.width,
								iconWrapper.zIndex
							]"
						>
							<tools-icon v-bind="{ ...item.icon }" svg-class="sm:h-[100px] h-[50px] xs:h-[60px]" />
						</div>
					</div>

					<div class="pt-5 lg:pt-10">
						<strong class="font-semibold text-xl py-3 block">
							{{ item.title }}
						</strong>
						<p class="text-gray-600" v-html="item.description" />
					</div>
				</div>
			</div>

			<slot name="btn">
				<lazy-ui-btn
					name="onboard"
					class="bg-main mx-auto text-white md:py-5"
					:ui="{}"
					@click="$emit('trigger')"
				>
					Onboard your business in <span class="font-[800]"> 5 </span> minutes or less
				</lazy-ui-btn>
			</slot>
		</div>
	</section>
</template>

<style scoped>
	.stroke-text {
		-webkit-font-smoothing: antialiased;
		-moz-osx-font-smoothing: grayscale;
		text-rendering: optimizeLegibility;
	}

	@media (prefers-color-scheme: dark) {
		.stroke-text {
			-webkit-text-stroke: 3px #4aa695;
		}
	}
</style>
